import './AddPage.scss';
import { Button, Card, Checkbox, Dropdown, Icon, Input, Loader, Textarea, Typography } from '@design-system/index';
import { Formik } from 'formik';
import { InfoLine, ProductsCatalogLine } from '@icons';
import { useAddPageVM } from './useAddPageVM';
import { ProductToCreate } from '@services/product/product/product.types';
import { CATEGORY_TYPE } from '@utils/enumTranslation';
import { CreatableDropdown } from '@design-system/components/Dropdown/CreatableDropdown';
import { FileUpload } from '@components/index';
import { CdnFile } from '@services/core/cdn/file/file';
import { AutocompleteInput } from '@components/AutocompleteInput/AutocompleteInput';
import { useGetETicketTypesMutation } from '@services/product/eTicketType/e-ticket-type.endpoints';
import moment from 'moment';

export default function AddPage() {
    const { submitForm, isLoading, product, isEdit, isTypeEvent, deleteDocument, addDocument, productDocuments } = useAddPageVM();

    if (isLoading && isEdit) {
        return <Loader className="full-container-loader" />;
    }

    return (
        <div className="add-product-container">
            <Card
                isLoading={isLoading}
                headerType="full"
                showBack
                headerTitle={isEdit ? 'Modification produit' : 'Création nouveau produit'}
                headerIcon={<Icon icon={<ProductsCatalogLine />} size={20} color="primary" />}
            >
                <>
                    <Formik
                        initialValues={
                            product || {
                                category: isTypeEvent ? 'spectacles' : '',
                                subcategories: [],
                                eticketType: undefined,
                                presentation: {
                                    title: '',
                                    shortDescription: '',
                                    description: ''
                                },
                                stock: {
                                    alertThreshold: '',
                                    alertMails: []
                                },
                                price: {
                                    tva: 0,
                                    priceHT: '',
                                    priceTTC: '',
                                    isPerPerson: false,
                                    isStartingPrice: false,
                                    priceType: '',
                                    reductionAmount: '',
                                    reductionType: '',
                                    description: '',
                                    promotion: {
                                        isPromotion: false,
                                        priceTTC: '',
                                        priceHT: '',
                                        startDateTime: '',
                                        endDateTime: ''
                                    }
                                },
                                coverImage: undefined,
                                coverImageId: undefined,

                                images: [],

                                communicationImage: undefined,
                                communicationImageId: undefined,

                                displayManagement: {
                                    isDisabled: false,
                                    isFeatured: false
                                },
                                documents: [],
                                event: {
                                    startDateTime: '',
                                    endDateTime: '',
                                    address: '',
                                    city: '',
                                    zipCode: ''
                                }
                            }
                        }
                        enableReinitialize
                        onSubmit={async (values: ProductToCreate) => {
                            await submitForm(values);
                        }}
                    >
                        {({ values, handleChange, handleSubmit, setFieldValue, isSubmitting }) => {
                            return (
                                <form onSubmit={handleSubmit} className="form">
                                    <div className="form-section-container">
                                        <div className="form-section">
                                            <Typography intrinsic="h3">Catégorie</Typography>
                                            <div className="form-section-fields">
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Dropdown
                                                            label="Catégorie"
                                                            required
                                                            options={Object.entries(CATEGORY_TYPE).map(([key, value]) => ({
                                                                id: key,
                                                                label: value
                                                            }))}
                                                            value={values.category}
                                                            onOptionChange={(option) => {
                                                                setFieldValue('category', option.id);
                                                                // Vider les sous-catégories quand on change la catégorie principale
                                                                setFieldValue('subcategories', []);
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        {values.category === 'e-billets' && (
                                                            <Dropdown
                                                                label="Type de E-billet"
                                                                isSearchable
                                                                mutationHook={useGetETicketTypesMutation}
                                                                required
                                                                value={values.eticketType}
                                                                onOptionChange={(option) => setFieldValue('eticketType', option)}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                                {values.category && (
                                                    <div className="form-line">
                                                        <div className="form-group">
                                                            <CreatableDropdown
                                                                label="Sous-catégories"
                                                                name="subcategories"
                                                                value={values.subcategories || []}
                                                                options={Object.entries(CATEGORY_TYPE)
                                                                    .filter(([key]) => key !== values.category)
                                                                    .map(([, value]) => value)}
                                                                onOptionChange={(option) => {
                                                                    setFieldValue('subcategories', option);
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="form-group"></div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="form-section">
                                            <Typography intrinsic="h3">Présentation</Typography>
                                            <div className="form-section-fields">
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Input
                                                            label="Titre"
                                                            required
                                                            name="presentation.title"
                                                            value={values.presentation.title}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Textarea
                                                            label="Accroche (sous-titre)"
                                                            height={75}
                                                            name="presentation.shortDescription"
                                                            value={values.presentation.shortDescription ?? ''}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Textarea
                                                            label="Description"
                                                            height={220}
                                                            wysiwyg
                                                            name="presentation.description"
                                                            value={values.presentation.description ?? ''}
                                                            onChange={(value) => {
                                                                setFieldValue('presentation.description', value);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {values.category === 'e-billets' && (
                                            <div className="form-section">
                                                <Typography intrinsic="h3">Alerte stock</Typography>
                                                <div className="form-section-fields">
                                                    <div className="form-line">
                                                        <div className="form-group">
                                                            <Input
                                                                label="Seuil d'alerte"
                                                                type="number"
                                                                name="stock.alertThreshold"
                                                                value={values.stock.alertThreshold}
                                                                onChange={handleChange}
                                                            />
                                                        </div>
                                                        <div className="form-group"></div>
                                                    </div>
                                                    <div className="form-line">
                                                        <div className="form-group">
                                                            <CreatableDropdown
                                                                label="Mails d'alerte"
                                                                name="stock.alertMails"
                                                                value={values.stock.alertMails}
                                                                onOptionChange={(option) => {
                                                                    setFieldValue('stock.alertMails', option);
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <div className="form-section">
                                            <Typography intrinsic="h3">Tarifs</Typography>
                                            <div className="form-section-fields">
                                                {values.category == 'offres_partenaires' && (
                                                    <>
                                                        <div className="form-line">
                                                            <div className="form-group">
                                                                <Dropdown
                                                                    label="Type de tarif"
                                                                    required
                                                                    options={[
                                                                        { id: 'reduction', label: 'Réduction' },
                                                                        { id: 'fixed', label: 'Prix fixe' },
                                                                        { id: 'free', label: 'Gratuit' },
                                                                        { id: 'advantage', label: 'Avantage' }
                                                                    ]}
                                                                    value={values.price.priceType}
                                                                    onOptionChange={(option) => {
                                                                        setFieldValue('price.priceType', option.id);
                                                                        setFieldValue('price.reductionType', 'percentage');
                                                                    }}
                                                                />
                                                            </div>
                                                            <div className="form-group">
                                                                {values.price.priceType === 'reduction' && (
                                                                    <Dropdown
                                                                        label="Type de réduction"
                                                                        required
                                                                        options={[
                                                                            { id: 'percentage', label: 'Pourcentage' },
                                                                            { id: 'amount', label: 'Montant' }
                                                                        ]}
                                                                        value={values.price.reductionType}
                                                                        onOptionChange={(option) => {
                                                                            setFieldValue('price.reductionType', option.id);
                                                                        }}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>

                                                        {values.price.priceType === 'reduction' && (
                                                            <div className="form-line">
                                                                <div className="form-group">
                                                                    <Input
                                                                        label="Montant de la réduction"
                                                                        required
                                                                        type="number"
                                                                        rightContent={
                                                                            values.price.reductionType === 'percentage' ? '%' : '€'
                                                                        }
                                                                        name="price.reductionAmount"
                                                                        value={values.price.reductionAmount}
                                                                        onChange={handleChange}
                                                                    />
                                                                </div>
                                                                <div className="form-group"></div>
                                                            </div>
                                                        )}
                                                    </>
                                                )}

                                                {(values.category != 'offres_partenaires' ||
                                                    (values.category == 'offres_partenaires' && values.price.priceType == 'fixed')) && (
                                                    <>
                                                        {/* <div className="form-line">
                                                            <div className="form-group">
                                                                <Dropdown
                                                                    label="TVA"
                                                                    required={values.category == 'e-billets'}
                                                                    options={[
                                                                        { id: 0, label: '0%' },
                                                                        { id: 5.5, label: '5.5%' },
                                                                        { id: 10, label: '10%' },
                                                                        { id: 20, label: '20%' }
                                                                    ]}
                                                                    value={values.price.tva}
                                                                    onOptionChange={(option) => {
                                                                        setFieldValue('price.tva', option.id);
                                                                        if (
                                                                            values.price.priceHT !== '' &&
                                                                            values.price.priceHT !== undefined
                                                                        ) {
                                                                            setFieldValue(
                                                                                'price.priceTTC',
                                                                                Number(
                                                                                    (
                                                                                        values.price.priceHT *
                                                                                        (1 + (option.id as number) / 100)
                                                                                    ).toFixed(2)
                                                                                )
                                                                            );
                                                                        }
                                                                        if (
                                                                            values.price.promotion.priceHT !== '' &&
                                                                            values.price.promotion.priceHT !== undefined
                                                                        ) {
                                                                            setFieldValue(
                                                                                'price.promotion.priceTTC',
                                                                                Number(
                                                                                    (
                                                                                        values.price.promotion.priceHT *
                                                                                        (1 + (option.id as number) / 100)
                                                                                    ).toFixed(2)
                                                                                )
                                                                            );
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                            <div className="form-group"></div>
                                                        </div> */}
                                                        <div className="form-line" style={{ gap: '16px' }}>
                                                            {/* <div className="form-group">
                                                                <Input
                                                                    label="Prix HT"
                                                                    type="number"
                                                                    required={values.category == 'e-billets'}
                                                                    rightContent={`€`}
                                                                    name="price.priceHT"
                                                                    value={values.price.priceHT}
                                                                    onChange={(e) => {
                                                                        setFieldValue('price.priceHT', e.target.value);
                                                                        if (values.price.tva !== '' && e.target.value !== '') {
                                                                            setFieldValue(
                                                                                'price.priceTTC',
                                                                                Number(
                                                                                    (
                                                                                        (e.target.value as unknown as number) *
                                                                                        (1 + (values.price.tva as number) / 100)
                                                                                    ).toFixed(2)
                                                                                )
                                                                            );
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                            <div>
                                                                <Icon icon={<Switch />} size={20} color="secondary" />
                                                            </div> */}
                                                            <div className="form-group">
                                                                <Input
                                                                    label="Prix"
                                                                    rightContent={`€`}
                                                                    required={values.category == 'e-billets'}
                                                                    type="number"
                                                                    name="price.priceTTC"
                                                                    value={values.price.priceTTC}
                                                                    onChange={(e) => {
                                                                        setFieldValue('price.priceTTC', e.target.value);
                                                                        if (values.price.tva !== '' && e.target.value !== '') {
                                                                            setFieldValue(
                                                                                'price.priceHT',
                                                                                Number(
                                                                                    (
                                                                                        (e.target.value as unknown as number) /
                                                                                        (1 + (values.price.tva as number) / 100)
                                                                                    ).toFixed(2)
                                                                                )
                                                                            );
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="form-line">
                                                            <div>
                                                                <Checkbox
                                                                    checked={values.price.isPerPerson}
                                                                    onChange={(checked) => {
                                                                        setFieldValue('price.isPerPerson', checked);
                                                                    }}
                                                                >
                                                                    <Typography intrinsic="p" color="secondary">
                                                                        Par personne
                                                                    </Typography>
                                                                </Checkbox>
                                                            </div>
                                                            <div>
                                                                <Checkbox
                                                                    checked={values.price.isStartingPrice}
                                                                    onChange={(checked) => {
                                                                        setFieldValue('price.isStartingPrice', checked);
                                                                    }}
                                                                >
                                                                    <Typography intrinsic="p" color="secondary">
                                                                        A partir de
                                                                    </Typography>
                                                                </Checkbox>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}

                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Textarea
                                                            label="Description du tarif"
                                                            height={220}
                                                            wysiwyg
                                                            name="price.description"
                                                            value={values.price.description ?? ''}
                                                            onChange={(value) => {
                                                                setFieldValue('price.description', value);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {values.category != 'offres_partenaires' && (
                                            <>
                                                <div className="form-section">
                                                    <Typography intrinsic="h3">Promotions</Typography>
                                                    <div className="form-section-fields">
                                                        <div className="form-line">
                                                            <div className="form-group">
                                                                <Checkbox
                                                                    checked={values.price.promotion.isPromotion}
                                                                    onChange={(checked) => {
                                                                        setFieldValue('price.promotion.isPromotion', checked);
                                                                    }}
                                                                >
                                                                    <Typography intrinsic="p" color="secondary">
                                                                        En promotion
                                                                    </Typography>
                                                                </Checkbox>
                                                            </div>
                                                        </div>
                                                        <div className="form-line" style={{ gap: '16px' }}>
                                                            {/* <div className="form-group">
                                                                <Input
                                                                    label="Prix HT"
                                                                    required={values.price.promotion.isPromotion}
                                                                    type="number"
                                                                    rightContent={`€`}
                                                                    name="price.promotion.priceHT"
                                                                    value={values.price.promotion.priceHT}
                                                                    onChange={(e) => {
                                                                        setFieldValue('price.promotion.priceHT', e.target.value);
                                                                        if (values.price.tva !== '' && e.target.value !== '') {
                                                                            setFieldValue(
                                                                                'price.promotion.priceTTC',
                                                                                Number(
                                                                                    (
                                                                                        (e.target.value as unknown as number) *
                                                                                        (1 + (values.price.tva as number) / 100)
                                                                                    ).toFixed(2)
                                                                                )
                                                                            );
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                            <div>
                                                                <Icon icon={<Switch />} size={20} color="secondary" />
                                                            </div> */}
                                                            <div className="form-group">
                                                                <Input
                                                                    label="Prix"
                                                                    required={values.price.promotion.isPromotion}
                                                                    rightContent={`€`}
                                                                    type="number"
                                                                    name="price.promotion.priceTTC"
                                                                    value={values.price.promotion.priceTTC}
                                                                    onChange={(e) => {
                                                                        setFieldValue('price.promotion.priceTTC', e.target.value);
                                                                        if (values.price.tva !== '' && e.target.value !== '') {
                                                                            setFieldValue(
                                                                                'price.promotion.priceHT',
                                                                                Number(
                                                                                    (
                                                                                        (e.target.value as unknown as number) /
                                                                                        (1 + (values.price.tva as number) / 100)
                                                                                    ).toFixed(2)
                                                                                )
                                                                            );
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="form-line">
                                                            <div className="form-group">
                                                                <Input
                                                                    label="Date de début"
                                                                    required={values.price.promotion.isPromotion}
                                                                    type="date"
                                                                    name="price.promotion.startDateTime"
                                                                    value={values.price.promotion.startDateTime}
                                                                    onChange={handleChange}
                                                                />
                                                            </div>
                                                            <div className="form-group">
                                                                <Input
                                                                    label="Date de fin"
                                                                    // required={values.price.promotion.isPromotion}
                                                                    type="date"
                                                                    name="price.promotion.endDateTime"
                                                                    value={values.price.promotion.endDateTime}
                                                                    onChange={handleChange}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                        {(values.category == 'spectacles' || values.category == 'sorties_voyages') && (
                                            <>
                                                <div className="form-section">
                                                    <Typography intrinsic="h3">Détails de l'évènement</Typography>
                                                    <div className="form-section-fields">
                                                        <div className="form-line">
                                                            <Typography intrinsic="p" flex color="secondary">
                                                                <Icon icon={<InfoLine />} size={20} color="secondary" />
                                                                <span>
                                                                    Vous avez choisi la catégorie suivante pour votre produit :{' '}
                                                                    <b>
                                                                        {values.category == 'spectacles'
                                                                            ? 'Spectacles'
                                                                            : 'Sorties & voyages'}
                                                                    </b>
                                                                    .<br />
                                                                    Veuillez remplir les champs suivants pour procéder à la création de
                                                                    l'événement dans l'agenda.
                                                                </span>
                                                            </Typography>
                                                        </div>
                                                        <div className="form-line">
                                                            <div className="form-group">
                                                                <Input
                                                                    label="Date de début"
                                                                    required
                                                                    type="datetime-local"
                                                                    name="event.startDateTime"
                                                                    value={
                                                                        values.event.startDateTime
                                                                            ? moment(values.event.startDateTime).format('YYYY-MM-DDTHH:mm')
                                                                            : ''
                                                                    }
                                                                    onChange={handleChange}
                                                                />
                                                            </div>
                                                            <div className="form-group">
                                                                <Input
                                                                    label="Date de fin"
                                                                    // required
                                                                    type="datetime-local"
                                                                    name="event.endDateTime"
                                                                    value={
                                                                        values.event.endDateTime
                                                                            ? moment(values.event.endDateTime).format('YYYY-MM-DDTHH:mm')
                                                                            : ''
                                                                    }
                                                                    onChange={handleChange}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="form-line">
                                                            <div className="form-group">
                                                                <AutocompleteInput
                                                                    onPlaceSelect={(place) => {
                                                                        setFieldValue('event.address', place.address);
                                                                        setFieldValue('event.city', place.city);
                                                                        setFieldValue('event.zipCode', place.postal_code);
                                                                    }}
                                                                >
                                                                    <Input
                                                                        label="Adresse"
                                                                        required={values.category == 'spectacles'}
                                                                        name="event.address"
                                                                        value={values.event.address}
                                                                        onChange={handleChange}
                                                                    />
                                                                </AutocompleteInput>
                                                            </div>
                                                        </div>
                                                        <div className="form-line">
                                                            <div className="form-group">
                                                                <Input
                                                                    label="Ville"
                                                                    required={values.category == 'spectacles'}
                                                                    name="event.city"
                                                                    value={values.event.city}
                                                                    onChange={handleChange}
                                                                />
                                                            </div>
                                                            <div className="form-group">
                                                                <Input
                                                                    label="Code postal"
                                                                    required={values.category == 'spectacles'}
                                                                    name="event.zipCode"
                                                                    value={values.event.zipCode}
                                                                    onChange={handleChange}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        <div className="form-section">
                                            <Typography intrinsic="h3">Photos</Typography>
                                            <div className="form-section-fields">
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <FileUpload
                                                            width="100%"
                                                            height="248px"
                                                            caption="Photo de couverture"
                                                            subtitle={
                                                                <>
                                                                    Photo au format paysage rectangulaire.
                                                                    <br />
                                                                    Formats acceptés : PNG, JPEG. Ratio conseillé : 1920x454 pixels.
                                                                </>
                                                            }
                                                            isMultiple={true}
                                                            isImage={true}
                                                            files={values.coverImage}
                                                            onSuccess={(files: CdnFile[]) => {
                                                                setFieldValue('coverImageId', files[0]?.id);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <div className="form-group-file-upload-grid">
                                                            <FileUpload
                                                                width="465px"
                                                                height="310px"
                                                                caption="Photo illustratives"
                                                                subtitle={
                                                                    <>
                                                                        Photo au format paysage rectangulaire.
                                                                        <br />
                                                                        Formats acceptés : PNG, JPEG. Ratio conseillé : 1280x720 pixels.
                                                                    </>
                                                                }
                                                                isMultiple={false}
                                                                isImage={true}
                                                                files={values.images?.[0]}
                                                                position={0}
                                                                onSuccess={(files: CdnFile[]) => {
                                                                    const newImages = values.images ? [...values.images] : [];
                                                                    newImages[0] = files[0];
                                                                    setFieldValue('images', newImages);
                                                                }}
                                                            />
                                                            <FileUpload
                                                                width="310px"
                                                                height="310px"
                                                                variant="light"
                                                                isMultiple={false}
                                                                isImage={true}
                                                                files={values.images?.[1]}
                                                                position={1}
                                                                onSuccess={(files: CdnFile[]) => {
                                                                    const newImages = values.images ? [...values.images] : [];
                                                                    newImages[1] = files[0];
                                                                    setFieldValue('images', newImages);
                                                                }}
                                                            />
                                                            <div className="form-group-file-upload-grid-items">
                                                                {[
                                                                    ...Array(
                                                                        Math.max(
                                                                            2,
                                                                            (values.images?.filter((img) => img?.id != null).length || 0) -
                                                                                1
                                                                        )
                                                                    )
                                                                ].map((_, index) => (
                                                                    <FileUpload
                                                                        key={index}
                                                                        width="206px"
                                                                        height="148px"
                                                                        variant="light"
                                                                        isMultiple={false}
                                                                        isImage={true}
                                                                        position={index + 2}
                                                                        files={values.images?.[index + 2]}
                                                                        onSuccess={(files: CdnFile[]) => {
                                                                            const newImages = values.images ? [...values.images] : [];
                                                                            newImages[index + 2] = files[0];
                                                                            setFieldValue('images', newImages);
                                                                        }}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                {values.category == 'sorties_voyages' && (
                                                    <>
                                                        <div className="form-line">
                                                            <div className="form-group">
                                                                <FileUpload
                                                                    width="377px"
                                                                    height="533px"
                                                                    caption="Affiche communication"
                                                                    subtitle={
                                                                        <>
                                                                            Photos au format portait rectangulaire.
                                                                            <br />
                                                                            Formats acceptés : PNG, JPEG.
                                                                            <br />
                                                                            Ratio conseillé : 720x1280 pixels.
                                                                        </>
                                                                    }
                                                                    isMultiple={false}
                                                                    isImage={true}
                                                                    files={values.communicationImage}
                                                                    onSuccess={(files: CdnFile[]) => {
                                                                        setFieldValue('communicationImageId', files[0]?.id);
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        <div className="form-section">
                                            <Typography intrinsic="h3">Documents</Typography>
                                            <div className="form-section-fields">
                                                {values && (
                                                    <div className="form-line">
                                                        <FileUpload
                                                            width="100%"
                                                            height="214px"
                                                            isMultiple={true}
                                                            isImage={false}
                                                            table={true}
                                                            accept="application/pdf"
                                                            files={(productDocuments as CdnFile[]) || []}
                                                            onOneSuccess={(file: CdnFile) => {
                                                                addDocument(file);
                                                            }}
                                                            onOneDelete={(file: CdnFile) => {
                                                                deleteDocument(file.id);
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="form-section">
                                            <Typography intrinsic="h3">Gestion d'affichage</Typography>
                                            <div className="form-section-fields">
                                                <div className="form-line">
                                                    <div>
                                                        <Checkbox
                                                            checked={values.displayManagement.isDisabled}
                                                            onChange={(checked) => {
                                                                setFieldValue('displayManagement.isDisabled', checked);
                                                            }}
                                                        >
                                                            <Typography intrinsic="p" color="secondary">
                                                                Produit désactivé
                                                            </Typography>
                                                        </Checkbox>
                                                    </div>
                                                    <div>
                                                        <Checkbox
                                                            checked={values.displayManagement.isFeatured}
                                                            onChange={(checked) => {
                                                                setFieldValue('displayManagement.isFeatured', checked);
                                                            }}
                                                        >
                                                            <Typography intrinsic="p" color="secondary">
                                                                À la une
                                                            </Typography>
                                                        </Checkbox>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="action-buttons">
                                        <Button type="submit" disabled={isSubmitting} variant="primary">
                                            {isEdit ? 'Valider la modification' : 'Valider la création'}
                                        </Button>
                                    </div>
                                </form>
                            );
                        }}
                    </Formik>
                </>
            </Card>
        </div>
    );
}
